import { isNullOrUndefined } from 'Utils/typeGuards';
import { CollectedStats } from '../index.types';

/**
 * Calculates statistics (mean and variance) from an array of numeric values
 * @param values - Array of numeric values
 * @returns Object containing mean and variance
 */
export const calculateStats = (
  values: number[]
): { mean: number; variance: number } | undefined => {
  if (values.length === 0) return undefined;

  const sum = values.reduce((acc, val) => acc + val, 0);
  const mean = sum / values.length;
  const variance =
    values.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) /
    values.length;

  return { mean, variance };
};

/**
 * Extracts audio and network metrics from collected WebRTC stats
 * @param metrics - Array of collected WebRTC stats
 * @returns Object containing arrays of jitter, packet loss, and RTT values
 */
export const extractAudioMetrics = (metrics: CollectedStats[]) => {
  const jitterValues: number[] = [];
  const packetLossValues: number[] = [];
  const rttValues: number[] = [];

  metrics.forEach((stat) => {
    if (stat.basicStats) {
      !isNullOrUndefined(stat.basicStats.jitter) &&
        jitterValues.push(stat.basicStats.jitter);
      !isNullOrUndefined(stat.basicStats.packetsLost) &&
        packetLossValues.push(stat.basicStats.packetsLost);
      !isNullOrUndefined(stat.basicStats.roundTripTime) &&
        rttValues.push(stat.basicStats.roundTripTime * 1000); // Convert to ms
    }
  });

  return { jitterValues, packetLossValues, rttValues };
};

/**
 * Calculates R-value (transmission rating factor) based on ITU-T G.107 E-Model
 * @see ITU-T G.107: https://www.itu.int/rec/T-REC-G.107/en
 *
 * ## 📌 ITU-T G.107 E-Model Enhancements for MOS Calculation
 *
 * | **Improvement**                     | **Reference**                                         | **Description** |
 * |--------------------------------------|------------------------------------------------------|----------------|
 * | **RTT Impairment Factor (Id)**       | [ITU-T G.107](https://www.itu.int/rec/T-REC-G.107/en) | Adjusts penalty based on excessive RTT, reducing over-penalization. |
 * | **Packet Loss Impairment (Ie-eff)**  | [ITU-T G.113](https://www.itu.int/rec/T-REC-G.113/en) | Uses logarithmic model with an upper bound for better packet loss penalty calculation. |
 * | **Jitter-based Penalty (Ij)**        | [ITU-T G.108](https://www.itu.int/rec/T-REC-G.108/en) | Takes jitter **variance** into account, better handling network spikes. |
 * | **ITU-T G.107 MOS Mapping**         | [ITU-T G.107](https://www.itu.int/rec/T-REC-G.107/en) | Improved MOS calculation formula ensures better differentiation of quality levels. |
 *
 * @param jitterStats - Jitter statistics object with mean and variance
 * @param packetLossStats - Packet loss statistics object with mean and variance
 * @param rttStats - Round-trip time statistics object with mean and variance
 * @returns R-value for audio quality
 */
export const calculateRValue = (
  jitterStats: { mean: number; variance: number } | undefined,
  packetLossStats: { mean: number; variance: number } | undefined,
  rttStats: { mean: number; variance: number } | undefined
): number | undefined => {
  if (
    isNullOrUndefined(jitterStats) ||
    isNullOrUndefined(packetLossStats) ||
    isNullOrUndefined(rttStats)
  )
    return undefined;

  const BASE_R_VALUE = 93.2; // Default R0 value for wideband speech

  // RTT impairment factor (Id)
  // https://www.voiptroubleshooter.com/diagnosis/emodel.html
  const RTT_FACTOR = 0.024;
  const RTT_THRESHOLD = 177.3;
  const RTT_EXCESS_FACTOR = 0.11;
  const Id =
    RTT_FACTOR * rttStats.mean +
    RTT_EXCESS_FACTOR *
      (rttStats.mean - RTT_THRESHOLD) *
      (rttStats.mean > RTT_THRESHOLD ? 1 : 0);

  // Improved Packet Loss Impairment (Ie-eff)
  // ITU-T G.113 Packet Loss Effect: https://www.itu.int/rec/T-REC-G.113/en
  const PACKET_LOSS_FACTOR = 30;
  const PACKET_LOSS_MULTIPLIER = 15;
  const Ie_eff =
    PACKET_LOSS_FACTOR *
    Math.log(
      1 + (PACKET_LOSS_MULTIPLIER * Math.min(packetLossStats.mean, 99.9)) / 100
    );

  // Jitter-based penalty (Ij) with variance consideration
  // ITU-T G.108: https://www.itu.int/rec/T-REC-G.108/en
  const JITTER_FACTOR = 0.2;
  const Ij =
    JITTER_FACTOR * jitterStats.mean + 0.1 * Math.sqrt(jitterStats.variance); // Extra weight for jitter spikes

  // Compute final R-Value with all impairment factors
  const R = BASE_R_VALUE - Id - Ie_eff - Ij;

  // Prevent R-value from going below zero
  return Math.max(0, R);
};

/**
 * Converts R-value to MOS (Mean Opinion Score) using ITU-T G.107 mapping
 * @see ITU-T G.107: https://www.itu.int/rec/T-REC-G.107/en
 * @param rValue - R-value (transmission rating factor)
 * @returns MOS score between 1.0 and 5.0
 */
export const rValueToMos = (rValue: number): number => {
  if (rValue <= 0) {
    return 1.0;
  }

  if (rValue > 100) {
    // Handle very high R-values
    return 5.0;
  }

  // Formula to convert R-value to MOS
  // MOS = 1 + 0.035*R + 7*10^-6*R*(R-60)*(100-R)
  return rValue > 0
    ? 1 +
        0.035 * rValue +
        7 * 0.000001 * rValue * (rValue - 60) * (100 - rValue)
    : 1.0;
};

/**
 * Calculates MOS score based on collected metrics using the ITU-T G.107 E-Model
 * @see ITU-T G.107: https://www.itu.int/rec/T-REC-G.107/en
 * @param metrics - Array of collected stats
 * @returns MOS score between 1.0 and 5.0
 */
export const calculateMosScore = (
  metrics: CollectedStats[]
): number | undefined => {
  if (metrics.length === 0) return undefined;

  const { jitterValues, packetLossValues, rttValues } =
    extractAudioMetrics(metrics);

  const jitterStats = calculateStats(jitterValues);
  const packetLossStats = calculateStats(packetLossValues);
  const rttStats = calculateStats(rttValues);

  const rValue = calculateRValue(jitterStats, packetLossStats, rttStats);

  if (isNullOrUndefined(rValue)) {
    return undefined;
  }

  return parseFloat(rValueToMos(rValue).toFixed(2));
};

/**
 * Calculates packet loss rate from WebRTC RTP statistics
 *
 * This function analyzes packet loss patterns from collected WebRTC metrics to determine
 * the percentage of packets lost during audio transmission. It handles two scenarios:
 *
 * 1. **Single metric**: Calculates absolute packet loss rate from the total packets
 *    lost vs received in the current measurement
 * 2. **Multiple metrics**: Calculates relative packet loss rate by comparing the most
 *    recent metric with the second-to-last metric. This is necessary because WebRTC
 *    packet metrics are cumulative from the beginning of the call, so to provide
 *    meaningful real-time feedback to users, we need to calculate the packet loss
 *    that occurred only in the most recent interval rather than the entire call duration.
 *
 * @param metrics - Array of collected WebRTC statistics containing inbound audio data
 * @returns Packet loss percentage as a number between 0-100, where 0 indicates no packet loss
 *          and higher values indicate increasing packet loss severity. It also returns 0 if the metrics are invalid/undefined.
 */
export const calculatePacketLossRate = (metrics: CollectedStats[]): number => {
  if (!metrics?.length) return 0;

  const mostRecentMetric = metrics[metrics.length - 1];
  if (
    isNullOrUndefined(mostRecentMetric?.basicStats?.packetsReceived) ||
    isNullOrUndefined(mostRecentMetric?.basicStats?.packetsLost)
  )
    return 0;

  // For single metric, calculate absolute packet loss rate
  if (metrics.length === 1) {
    const { packetsLost = 0, packetsReceived = 0 } =
      mostRecentMetric.basicStats;
    const totalPackets = packetsLost + packetsReceived;

    return totalPackets > 0 ? (packetsLost / totalPackets) * 100 : 0;
  }

  // For multiple metrics, calculate relative packet loss rate
  // Use the second-to-last metric for comparison
  const olderMetricIndex = metrics.length - 2;
  const olderMetric = metrics[olderMetricIndex];

  if (
    isNullOrUndefined(olderMetric?.basicStats?.packetsReceived) ||
    isNullOrUndefined(olderMetric?.basicStats?.packetsLost)
  )
    return 0;

  // Calculate packet differences between recent and older metrics
  const packetsLostDiff =
    mostRecentMetric.basicStats.packetsLost -
    olderMetric.basicStats.packetsLost;
  const packetsReceivedDiff =
    mostRecentMetric.basicStats.packetsReceived -
    olderMetric.basicStats.packetsReceived;

  const totalPacketsDiff = packetsLostDiff + packetsReceivedDiff;

  return totalPacketsDiff > 0 ? (packetsLostDiff / totalPacketsDiff) * 100 : 0;
};

/**
 * Calculates averages for metrics with undefined value handling
 * @param metrics Array of metrics to process
 * @param extractValue Function to extract the value from each metric
 * @returns Average value or undefined if no valid values found
 */
export const calculateMetricAverage = (
  metrics: CollectedStats[],
  extractValue: (metric: CollectedStats) => number | undefined
): number | undefined => {
  const { sum, count } = metrics.reduce(
    (acc, metric) => {
      const value = extractValue(metric);
      if (typeof value === 'number') {
        acc.sum += value;
        acc.count++;
      }
      return acc;
    },
    { sum: 0, count: 0 }
  );

  return count > 0 ? sum / count : undefined;
};